import React, { useState, useRef, useCallback } from 'react';
import { Box, Slider, IconButton, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ImageIcon from '@mui/icons-material/Image';

/**
 * Inline: just an icon button if image exists.
 * Fullscreen modal is handled by ShipmentSearchResults via onOpen callback.
 */
export const ShipmentImageButton = ({ hasImage, onClick }) => {
    if (!hasImage) return null;
    return (
        <IconButton size="small" onClick={onClick} color="primary" title="Bild anzeigen">
            <ImageIcon fontSize="small" />
        </IconButton>
    );
};

/**
 * Zoomable image component for use inside the fullscreen modal.
 * Hover-zoom follows cursor, zoom level adjustable via slider.
 */
const ShipmentImageZoom = ({ src, alt = 'Sendungsbild' }) => {
    const [zoom, setZoom] = useState(2.5);
    const [hovering, setHovering] = useState(false);
    const [origin, setOrigin] = useState({ x: 50, y: 50 });
    const containerRef = useRef(null);

    const handleMouseMove = useCallback((e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setOrigin({ x, y });
    }, []);

    if (!src) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                <Typography>Kein Bild vorhanden</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, height: '100%' }}>
            <Box
                ref={containerRef}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                onMouseMove={handleMouseMove}
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    cursor: 'zoom-in',
                    width: '100%',
                    minHeight: 300,
                    maxHeight: 'calc(100vh - 280px)',
                    bgcolor: '#fafafa',
                }}
            >
                <Box
                    component="img"
                    src={src}
                    alt={alt}
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        transition: hovering ? 'none' : 'transform 0.2s ease-out',
                        transform: hovering ? `scale(${zoom})` : 'scale(1)',
                        transformOrigin: `${origin.x}% ${origin.y}%`,
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 250 }}>
                <ZoomInIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Slider
                    value={zoom}
                    onChange={(_, v) => setZoom(v)}
                    min={1}
                    max={8}
                    step={0.5}
                    size="small"
                />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 32 }}>
                    {zoom}×
                </Typography>
            </Box>
        </Box>
    );
};

export default ShipmentImageZoom;
